import { useEffect, useState } from "react";

import About, { CollapsibleSection } from "./components/About";
import { AppHeader } from "./components/AppHeader";
import { AudioWaveform } from "./components/AudioWaveform";
import { RecorderControls } from "./components/RecorderControls";
import RecordingHistory from "./components/RecordingHistory";
import Settings from "./components/Settings";
import { SpeechRecognitionStatus } from "./components/SpeechRecognitionStatus";
import { TranscriptDisplay } from "./components/TranscriptDisplay";
import { MetaUpdater } from "./components/MetaUpdater";
import { useMicrophone } from "./hooks/useMicrophone";
import { useSpeechRecognition } from "./hooks/useSpeechRecognition";
import { useTranslation } from "./hooks/useTranslation";
import { audioStreamService } from "./services/audioStreamService";
import { recordingSession } from "./services/recordingSession";
import { useAppStore } from "./store/appStore";
import appStyles from "./styles/styles";
import { checkBrowserCapabilities } from "./utils/browserCapabilities";
import { detectBrowserLanguage, getSpeechRecognitionLanguage, updateDocumentLanguage } from "./utils/languageUtils";
import { getSessionId } from "./utils/sessionUtils";

export function App() {
	const { saveTranscriptionToFile } = useSpeechRecognition();
	const isRecording = useAppStore((state) => state.isRecording);
	const transcriptionText = useAppStore((state) => state.transcriptionText);
	const selectedLanguage = useAppStore((state) => state.selectedLanguage);
	const availableLanguages = useAppStore((state) => state.availableLanguages);
	const setSelectedLanguage = useAppStore((state) => state.setSelectedLanguage);
	const uiLanguage = useAppStore((state) => state.uiLanguage);
	const loadRecordingsFromStorage = useAppStore((state) => state.loadRecordingsFromStorage);

	const t = useTranslation();
	const setUILanguage = useAppStore((state) => state.setUILanguage);

	// Update HTML document language attribute when UI language changes
	useEffect(() => {
		updateDocumentLanguage(uiLanguage);
	}, [uiLanguage]);

	// 初回レンダリング時にブラウザの言語を検出し、セッションIDを初期化し、録音データを読み込む
	useEffect(() => {
		// SpeechRecognition APIの利用可能性とモバイルブラウザ対応状況をチェックしてstoreに保存
		const capabilities = checkBrowserCapabilities();
		const setIsSpeechRecognitionAvailable = useAppStore.getState().setIsSpeechRecognitionAvailable;
		const setIsUnsupportedMobileBrowser = useAppStore.getState().setIsUnsupportedMobileBrowser;

		setIsSpeechRecognitionAvailable(capabilities.speechRecognition);
		setIsUnsupportedMobileBrowser(capabilities.isUnsupportedMobile);

		// 初回起動時の言語設定
		if (!localStorage.getItem("uiLanguage")) {
			const detectedLanguage = detectBrowserLanguage();
			setUILanguage(detectedLanguage);

			// 日本語以外のブラウザ言語の場合、UIは英語、認識言語はブラウザ言語に合わせる
			if (detectedLanguage !== 'ja-JP') {
				// 認識言語をブラウザに合わせて設定
				const recognitionLang = getSpeechRecognitionLanguage(availableLanguages);
				setSelectedLanguage(recognitionLang);
			}
		}

		// セッションID初期化（まだ存在しない場合は生成される）
		getSessionId();

		// 既存のマイク許可状態を確認（プロンプトは出さない）
		audioStreamService.checkExistingPermission();

		// IndexedDBから録音データを読み込む
		loadRecordingsFromStorage();
	}, [setUILanguage, setSelectedLanguage, availableLanguages, loadRecordingsFromStorage]);

	const toggleRecording = async () => {
		// 状態遷移中（開始・停止処理中）は無視する（連打防止）
		const sessionState = recordingSession.getSessionState();
		if (sessionState === "starting" || sessionState === "stopping") {
			return;
		}

		if (sessionState === "recording") {
			await recordingSession.stop();
			return;
		}

		// QUIRK: マイク許可はユーザージェスチャ（このクリック）から取得する
		if (micPermission !== "granted") {
			const granted = await audioStreamService.ensurePermission(selectedMicrophoneId);
			if (!granted) {
				alert(t.micAccessError);
				return;
			}
		}

		try {
			await recordingSession.start({
				language: selectedLanguage,
				deviceId: selectedMicrophoneId || undefined,
			});
		} catch (error) {
			console.error("録音開始エラー:", error);
			alert(t.recordingStartError);
		}
	};

	// アコーディオン状態管理
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [aboutOpen, setAboutOpen] = useState(false);

	const { microphones, selectMicrophone, selectedMicrophoneId, micPermission } = useMicrophone();

	// 設定画面が開かれたらマイク一覧を最新化する。
	// 許可が未確認の場合はここで取得を試みる（QUIRK: 設定を開く操作もユーザージェスチャ）
	useEffect(() => {
		if (!settingsOpen) return;

		const currentPermission = useAppStore.getState().micPermission;
		if (currentPermission === "granted") {
			audioStreamService.refreshDevices();
		} else if (currentPermission === "unknown") {
			// 設定画面では明示的なエラーメッセージは出さない（ユーザーが意図的に拒否した可能性があるため）
			audioStreamService.ensurePermission();
		}
	}, [settingsOpen]);

	// 選択中のマイクのラベルを取得
	const getSelectedMicrophoneLabel = () => {
		// マイクが選択されている場合はそのマイク情報を表示
		if (selectedMicrophoneId) {
			const selectedMic = microphones.find((mic) => mic.deviceId === selectedMicrophoneId);
			// ラベルがあればそれを表示、なければデバイスIDの一部を表示
			if (selectedMic?.label) {
				return selectedMic.label;
			}
			return `${t.micLabel} ${selectedMicrophoneId.slice(0, 5)}...`;
		}

		// マイク許可はあるが、特定のマイクが選択されていない場合
		if (micPermission === "granted") {
			// マイクが検出されていれば最初のものを表示
			if (microphones.length > 0) {
				const firstMic = microphones[0];
				// ラベルがあればそれを表示、なければデフォルト表示
				return firstMic.label || t.defaultMicrophone;
			}

			// マイクリストはまだロード中かもしれないのでデフォルト表示
			return t.defaultMicrophone;
		}

		return "";
	};

	// 選択中の言語名を取得
	const getSelectedLanguageLabel = () => {
		if (!selectedLanguage) return "";
		const langInfo = availableLanguages.find((lang) => lang.code === selectedLanguage);
		return langInfo?.name || selectedLanguage;
	};

	const showMicInfo = Boolean(selectedMicrophoneId || micPermission === "granted");

	// メインのレンダリング
	return (
		<div className="min-h-screen bg-white dark:bg-dark-900 flex flex-col">
			<MetaUpdater />

			<AppHeader />
			<main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-8 md:px-0 pb-24">
				<RecorderControls
					onToggleRecording={toggleRecording}
					micLabel={showMicInfo ? getSelectedMicrophoneLabel() : null}
					languageLabel={selectedLanguage ? getSelectedLanguageLabel() : null}
				/>
				{/* 波形をリアルタイム文字起こしカードの上に常時表示 */}
				<div className="w-full flex flex-col items-center mb-6">
					<div style={{ maxWidth: 700, width: "100%" }}>
						<AudioWaveform />
					</div>
				</div>
				<div className={appStyles.controlsGrid}>
					<div className={appStyles.card}>
						<div className={appStyles.cardHeader}>
							<h2 className={appStyles.cardTitle}>
								<span className={appStyles.cardIcon}>📝</span>
								{t.realtimeTranscription}
							</h2>
						</div>
						<div className={appStyles.cardBody}>
							{/* SpeechRecognitionStatus は音声認識が利用できない場合のみ表示される */}
							<SpeechRecognitionStatus />
							<TranscriptDisplay />
							{/* 文字起こし保存ボタン */}
							<div className="mt-4">
								<button
									type="button"
									onClick={() => saveTranscriptionToFile()}
									className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-600 dark:hover:bg-blue-700"
									disabled={!transcriptionText}
									style={{
										opacity: !transcriptionText ? 0.5 : 1,
										cursor: !transcriptionText ? "not-allowed" : "pointer",
									}}
								>
									{t.downloadTranscription}
								</button>
							</div>
						</div>
					</div>
				</div>

				<div className="mt-12 relative">
					<div className={appStyles.card}>
						<div className={appStyles.cardHeader}>
							<h2 className={appStyles.cardTitle}>
								<span className={appStyles.cardIcon}>📜</span>
								{t.recordingHistory}
							</h2>
						</div>
						<div className={`${appStyles.cardBody} overflow-hidden`}>
							<RecordingHistory />
						</div>
					</div>
				</div>
				{/* Settings and About sections */}
				<div className="mt-12 mb-8 relative isolate">
					<CollapsibleSection
						title={t.settings}
						icon="⚙️"
						isOpen={settingsOpen}
						onToggle={() => setSettingsOpen((v) => !v)}
						id="settings"
					>
						<Settings
							isRecording={isRecording}
							microphones={microphones}
							selectedMicrophoneId={selectedMicrophoneId}
							selectMicrophone={selectMicrophone}
							selectedRecognitionLanguage={selectedLanguage}
							setSelectedRecognitionLanguage={setSelectedLanguage}
							availableLanguages={availableLanguages}
						/>
					</CollapsibleSection>
					<CollapsibleSection
						className="mt-6"
						title={t.aboutApp}
						icon="ℹ️"
						isOpen={aboutOpen}
						onToggle={() => setAboutOpen((v) => !v)}
						id="about"
					>
						<About />
					</CollapsibleSection>
				</div>
			</main>
		</div>
	);
}
